import React, { forwardRef, useContext, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import AnswerForm from 'views/Questions/components/AnswerForm';
import ChangeQuestionPanel from 'views/Questions/components/ChangeQuestionPanel';
import EditTextQuestion from 'views/Questions/components/EditTextQuestion';
import Breadcrumbs from 'views/Questions/components/Breadcrumbs';
import { subjects } from 'shared/constants/subjects';
import { grades } from 'shared/constants/grades';
import { UserQuestionProps } from 'views/Questions/components/UserQuestion/UserQuestion.types';
import dateInterval from 'utils/dateInterval';
import showNotification from 'utils/showNotification';
import sendReportHandler from 'views/Questions/utils/sendReportHandler';
import updateQuestion from 'views/Questions/api/updateQuestion';
import CourseLinksData from 'views/Questions/components/CourseLinksData';
import { appAuthContext } from 'context/AuthContext';
import styles from './UserQuestion.module.css';

const UserQuestion = forwardRef<HTMLDivElement, UserQuestionProps>(
  ({ userWriteAnswer, answerFromUser, data, userHasAnswer, courseLinksData }, ref) => {
    const { user } = useContext(appAuthContext);
    const router = useRouter();
    const categoryFromSubject =
      subjects.find((i) => i.link === data.category.slug) || subjects[0];
    const gradeFromSubject =
      grades.find((i) => i.value === data.grade.toString()) || grades[0];

    const categoryFromData = categoryFromSubject;
    const gradeFromData = gradeFromSubject;
    const [editQuestion, setEditQuestion] = useState(false);
    const [editedQuestionText, setEditedQuestionText] = useState(data.body);
    const [editedCategories, setEditedCategories] = useState(categoryFromSubject);
    const [editedGrade, setEditedGrade] = useState(gradeFromSubject);

    const scrollToUserAnswerHandler = () => {
      if (answerFromUser.current !== null) {
        answerFromUser.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    const updateQuestionHandler = async () => {
      const updateRes = await updateQuestion(
        data.slug,
        editedQuestionText,
        +editedCategories.id,
        +editedGrade.value,
      );
      if (updateRes && updateRes.success && updateRes?.slug) {
        setEditQuestion(false);
        return await router.push(updateRes.slug);
      }
      showNotification('error', 'Извини, произошла ошибка, попробуй позже');
    };

    const submitReportHandler = async () => {
      if (!user) {
        return showNotification(
          'info',
          'Чтобы отправить жалобу, войди или зарегистрируйся',
        );
      }
      await sendReportHandler('questions', data.slug);
    };

    return (
      <div className={styles.question}>
        <div className={styles.question__wrapper}>
          <Breadcrumbs
            categoryFromData={categoryFromData}
            gradeFromData={gradeFromData}
            edit={editQuestion}
            categories={editedCategories}
            setCategories={(value) => setEditedCategories(value)}
            grade={editedGrade}
            setGrade={(value) => setEditedGrade(value)}
          />
          <div className={styles.content} ref={ref}>
            <div className={styles.user}>
              <div className={styles.user__info}>
                <div className={styles.user__avatar}>
                  <Image alt="" src={data.authorAvatar} width={40} height={40} />
                </div>
                <span className={styles.user__name}>{data.authorName}</span>
                <span className={styles.user__date}>{dateInterval(data.createdAt)}</span>
              </div>
              {!data.userIsAuthor && (
                <div className={styles.report} onClick={() => submitReportHandler()}>
                  <Image
                    alt="Пожаловаться"
                    src="https://static.uchi.ru/files/otvety/report_flag.svg"
                    width={24}
                    height={24}
                  />
                  <span className={styles.report__text}>Пожаловаться</span>
                </div>
              )}
            </div>

            {/* Редактирование текста вопроса, автором вопроса */}
            {editQuestion && (
              <EditTextQuestion
                setEditQuestion={() => setEditQuestion(false)}
                editedQuestionText={editedQuestionText}
                setEditedQuestionText={(value) => setEditedQuestionText(value)}
                updateQuestionHandler={updateQuestionHandler}
              />
            )}
            {!editQuestion && (
              <>
                <h1 className={styles.question__text} data-qa-marker="qstn_txt">
                  {data.body}
                </h1>
                {/* Пользователь не автор и не отвечал на вопрос */}
                {!userHasAnswer && !data.userIsAuthor && (
                  <AnswerForm userWriteAnswer={userWriteAnswer} questionId={data.slug} />
                )}
                {/*  */}
                {/*Пользователь ответил на этот вопрос*/}
                {userHasAnswer && (
                  <div
                    className={styles.question__to_answer}
                    onClick={scrollToUserAnswerHandler}
                  >
                    {user?.avatar && (
                      <div className={styles.user__answer_avatar}>
                        <Image alt="avatar" src={user?.avatar} width={40} height={40} />
                      </div>
                    )}
                    Твой ответ уже тут
                    <span className={styles.question__to_answer_link}>К ответу</span>
                  </div>
                )}
                {/* */}
                {/* Пользователь автор вопрос и статус отклонен или на модерации */}
                {(data.questionState === 'moderating' ||
                  data.questionState === 'declined') &&
                  data.userIsAuthor && (
                    <ChangeQuestionPanel
                      type={data.questionState}
                      questionSlug={data.slug}
                      editQuestion={(value) => setEditQuestion(value)}
                    />
                  )}
                {/*  */}
              </>
            )}
          </div>
          {/* Подготовка к уроку */}
          {courseLinksData && courseLinksData.length > 0 && (
            <CourseLinksData courseLinksData={courseLinksData} />
          )}
        </div>
      </div>
    );
  },
);

UserQuestion.displayName = 'UserQuestion';

export default UserQuestion;
